import React, { useEffect, useState } from "react";
import { api, fmtDate } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { Fish, Wrench, TrendingUp, MapPin, AlertCircle, Calendar as CalendarIcon, ChevronLeft, ChevronRight, Users } from "lucide-react";
import { MapContainer, TileLayer, CircleMarker, Popup } from "react-leaflet";
import "leaflet/dist/leaflet.css";

const TABS = [
  { key: "heatmap",    icon: Fish,       label: "intel.tab.heatmap" },
  { key: "forecast",   icon: Wrench,     label: "intel.tab.forecast" },
  { key: "utilization", icon: TrendingUp, label: "intel.tab.utilization" },
  { key: "usage",      icon: Users,      label: "intel.tab.usage" },
];

export default function Intelligence() {
  const t = useT();
  const [tab, setTab] = useState("heatmap");

  return (
    <div className="space-y-6" data-testid="intelligence-page">
      <div>
        <div className="font-mono text-[10px] tracking-widest text-[#A78BFA]">{t("intel.subtitle")}</div>
        <h2 className="font-display text-2xl font-bold tracking-tight">{t("intel.title")}</h2>
        <p className="text-sm text-[#94A3B8] mt-1">{t("intel.intro")}</p>
      </div>

      <div className="flex flex-wrap gap-2" data-testid="intel-tabs">
        {TABS.map(tb => {
          const Icon = tb.icon;
          const active = tab === tb.key;
          return (
            <button
              key={tb.key} onClick={() => setTab(tb.key)}
              data-testid={`intel-tab-${tb.key}`}
              className={`px-3 py-2 rounded-md text-xs font-mono tracking-widest border flex items-center gap-2 transition-colors ${active ? "bg-[#007AFF]/15 border-[#007AFF]/50 text-[#F8FAFC]" : "bg-[#0B1221] border-[#1E293B] text-[#94A3B8] hover:border-[#334155]"}`}
            >
              <Icon className="w-3 h-3" />
              {t(tb.label)}
            </button>
          );
        })}
      </div>

      {tab === "heatmap" && <FishingHeatmap t={t} />}
      {tab === "forecast" && <MaintenanceForecast t={t} />}
      {tab === "utilization" && <UtilizationPanel t={t} />}
      {tab === "usage" && <UsageSplit t={t} />}
    </div>
  );
}

function UsageSplit({ t }) {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get("/intelligence/usage-split", { params: { year } })
      .then(r => setData(r.data))
      .finally(() => setLoading(false));
  }, [year]);

  const monthLabels = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const ownerColors = ["#4DA6FF", "#A78BFA", "#34D399", "#F59E0B", "#F87171"];
  const owners = data?.owners || [];
  const maxMonth = Math.max(1, ...owners.flatMap(o => o.by_month || []));
  const totalDays = owners.reduce((acc, o) => acc + o.total_days, 0);
  const sharedDays = data?.shared_days_count ?? data?.both_same_day_count ?? 0;
  const fmtDays = (n) => Number.isInteger(n) ? `${n}` : n.toFixed(1).replace(/\.0$/, "");

  return (
    <div className="space-y-4" data-testid="usage-panel">
      <div className="flex items-center gap-3 flex-wrap">
        <button onClick={() => setYear(y => y - 1)} className="p-2 rounded-md bg-[#0B1221] border border-[#1E293B] hover:border-[#334155]" data-testid="usage-prev-year"><ChevronLeft className="w-4 h-4" /></button>
        <div className="font-display text-xl font-bold tracking-tight" data-testid="usage-year">{year}</div>
        <button onClick={() => setYear(y => y + 1)} className="p-2 rounded-md bg-[#0B1221] border border-[#1E293B] hover:border-[#334155]" data-testid="usage-next-year"><ChevronRight className="w-4 h-4" /></button>
        <div className="ml-auto font-mono text-[10px] text-[#94A3B8]">{t("intel.usage.formula")}</div>
      </div>

      {loading && <div className="ledger-card p-12 text-center text-[#94A3B8]">{t("common.loading")}</div>}

      {!loading && owners.length === 0 && (
        <div className="ledger-card p-12 text-center text-[#94A3B8]" data-testid="usage-empty">
          <Users className="w-10 h-10 mx-auto mb-3 opacity-50" />
          <div className="text-sm">{t("intel.usage.empty")}</div>
        </div>
      )}

      {!loading && owners.length > 0 && (
        <>
          {/* Owner totals + comparison ratio */}
          <div className="grid md:grid-cols-2 gap-4">
            {owners.map((o, i) => {
              const pct = totalDays > 0 ? Math.round((o.total_days / totalDays) * 100) : 0;
              const color = ownerColors[i % ownerColors.length];
              return (
                <div key={o.user_id} className="ledger-card p-5" data-testid={`usage-owner-${i}`}>
                  <div className="flex items-baseline justify-between">
                    <div>
                      <div className="font-display text-xl font-bold">{o.name}</div>
                      <div className="font-mono text-[10px] text-[#64748B]">{o.email}</div>
                    </div>
                    <div className="font-display text-3xl font-bold" style={{ color }}>{fmtDays(o.total_days)}</div>
                  </div>
                  <div className="mt-3 font-mono text-[10px] text-[#94A3B8]">
                    {fmtDays(o.total_days)} {t("intel.usage.days")} · {pct}% {t("intel.usage.ofTotal")}
                    {o.dock_overnight_days > 0 && <> · <span className="text-[#A78BFA]">{o.dock_overnight_days} {t("intel.usage.dockNights")}</span></>}
                  </div>
                  <div className="mt-3 h-2 rounded-full bg-[#1E293B] overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${pct}%`, background: color }}></div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Monthly comparison bar chart */}
          <div className="ledger-card p-5">
            <div className="font-mono text-[10px] tracking-widest text-[#94A3B8] mb-4">{t("intel.usage.monthly")}</div>
            <div className="grid grid-cols-12 gap-2 items-end h-48">
              {monthLabels.map((m, idx) => (
                <div key={m} className="flex flex-col items-center gap-1" data-testid={`usage-month-${idx}`}>
                  <div className="flex-1 w-full flex items-end justify-center gap-0.5">
                    {owners.map((o, i) => {
                      const h = maxMonth > 0 ? (o.by_month[idx] / maxMonth) * 100 : 0;
                      return (
                        <div key={o.user_id} title={`${o.name}: ${fmtDays(o.by_month[idx])} days`}
                          className="w-1/2 rounded-t" style={{ height: `${h}%`, background: ownerColors[i % ownerColors.length], minHeight: o.by_month[idx] > 0 ? "2px" : "0" }} />
                      );
                    })}
                  </div>
                  <div className="font-mono text-[9px] text-[#64748B]">{m}</div>
                </div>
              ))}
            </div>
            <div className="mt-4 flex items-center gap-4 flex-wrap text-[10px] font-mono">
              {owners.map((o, i) => (
                <div key={o.user_id} className="flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-sm" style={{ background: ownerColors[i % ownerColors.length] }}></span>
                  <span className="text-[#F8FAFC]">{o.name}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="ledger-card p-4 grid grid-cols-2 gap-4 text-center" data-testid="usage-totals">
            <div>
              <div className="font-display text-2xl font-bold">{data.total_distinct_used_days}</div>
              <div className="font-mono text-[10px] text-[#94A3B8]">{t("intel.usage.distinctTotal")}</div>
            </div>
            <div>
              <div className="font-display text-2xl font-bold text-[#F59E0B]">{sharedDays}</div>
              <div className="font-mono text-[10px] text-[#94A3B8]">{t("intel.usage.bothSameDay")}</div>
            </div>
          </div>

          {sharedDays > 0 && (
            <div className="ledger-card p-3 flex items-start gap-2 border-[#A78BFA]/30" data-testid="usage-shared-nudge">
              <Users className="w-4 h-4 text-[#A78BFA] flex-shrink-0 mt-0.5" />
              <div className="text-xs text-[#94A3B8] leading-relaxed">
                <span className="text-[#F8FAFC] font-medium">+{sharedDays}</span> {t(sharedDays === 1 ? "intel.usage.sharedNudgeOne" : "intel.usage.sharedNudge")}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function FishingHeatmap({ t }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { (async () => {
    try {
      const { data } = await api.get("/intelligence/fishing-heatmap");
      setRows(data);
    } finally { setLoading(false); }
  })(); }, []);

  const center = rows.length > 0
    ? [rows[0].lat, rows[0].lng]
    : [45.528, 13.568]; // Piran default
  const max = Math.max(1, ...rows.map(r => r.count));

  if (loading) return <div className="ledger-card p-8 text-center text-[#94A3B8]">{t("common.loading")}</div>;

  if (rows.length === 0) {
    return (
      <div className="ledger-card p-12 text-center text-[#94A3B8]" data-testid="heatmap-empty">
        <Fish className="w-10 h-10 mx-auto mb-3 opacity-50" />
        <div className="text-sm">{t("intel.heatmap.empty")}</div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="heatmap-panel">
      <div className="ledger-card p-0 overflow-hidden" style={{ height: 480 }}>
        <MapContainer center={center} zoom={11} style={{ height: "100%", width: "100%", background: "#050A14" }} scrollWheelZoom>
          <TileLayer
            attribution='&copy; OpenStreetMap'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          {rows.map((r, i) => {
            const scale = r.count / max;
            const radius = 8 + scale * 22;
            // Color by rating: green high, amber mid, red low, grey no rating
            let color = "#94A3B8";
            if (r.avg_rating != null) {
              if (r.avg_rating >= 4) color = "#10B981";
              else if (r.avg_rating >= 3) color = "#F59E0B";
              else color = "#EF4444";
            }
            return (
              <CircleMarker
                key={i} center={[r.lat, r.lng]} radius={radius}
                pathOptions={{ color, fillColor: color, fillOpacity: 0.55, weight: 2 }}
              >
                <Popup>
                  <div className="text-xs" data-testid={`heatmap-popup-${i}`}>
                    <div className="font-bold">{r.count} {t("intel.heatmap.trips")}</div>
                    {r.avg_rating != null && <div>★ {r.avg_rating} {t("intel.heatmap.avgRating")}</div>}
                    {r.top_species.length > 0 && (
                      <div className="mt-1">
                        <div className="font-mono text-[9px] tracking-widest">{t("intel.heatmap.topSpecies")}</div>
                        <div>{r.top_species.map(s => `${s.name} (${s.count})`).join(", ")}</div>
                      </div>
                    )}
                    {r.spots.length > 0 && <div className="text-[#94A3B8] mt-1">{r.spots.join(" · ")}</div>}
                    {r.latest && <div className="font-mono text-[9px] text-[#94A3B8] mt-1">{t("intel.heatmap.latest")}: {fmtDate(r.latest)}</div>}
                  </div>
                </Popup>
              </CircleMarker>
            );
          })}
        </MapContainer>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {rows.slice(0, 6).map((r, i) => (
          <div key={i} className="ledger-card p-4" data-testid={`heatmap-spot-${i}`}>
            <div className="flex items-center justify-between mb-2">
              <span className="font-mono text-[10px] tracking-widest text-[#94A3B8] flex items-center gap-1">
                <MapPin className="w-3 h-3" /> {r.lat.toFixed(3)}, {r.lng.toFixed(3)}
              </span>
              <span className="badge-pill badge-fishing">{r.count} {t("intel.heatmap.trips")}</span>
            </div>
            {r.spots.length > 0 && <div className="text-sm font-medium mb-1">{r.spots[0]}</div>}
            <div className="flex items-center gap-3 text-xs">
              {r.avg_rating != null && <span className="text-[#FBBF24]">★ {r.avg_rating}</span>}
              {r.top_species.length > 0 && (
                <span className="text-[#94A3B8]">{r.top_species.map(s => s.name).join(", ")}</span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function MaintenanceForecast({ t }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { (async () => {
    try { const { data } = await api.get("/intelligence/maintenance-forecast"); setRows(data); }
    finally { setLoading(false); }
  })(); }, []);

  if (loading) return <div className="ledger-card p-8 text-center text-[#94A3B8]">{t("common.loading")}</div>;

  return (
    <div className="space-y-3" data-testid="forecast-panel">
      {rows.length === 0 && (
        <div className="ledger-card p-12 text-center text-[#94A3B8]">{t("intel.forecast.empty")}</div>
      )}
      {rows.map(r => {
        const overdue = r.avg_interval_days && r.days_since_last && r.days_since_last > r.avg_interval_days;
        return (
          <div key={r.asset_id} className="ledger-card p-5" data-testid={`forecast-${r.asset_id}`}>
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <div>
                <div className="font-display text-lg font-bold tracking-tight">{r.asset_name}</div>
                <div className="font-mono text-[10px] tracking-widest text-[#94A3B8] uppercase">{r.asset_type}</div>
              </div>
              {overdue ? (
                <span className="badge-pill badge-rejected flex items-center gap-1"><AlertCircle className="w-3 h-3" /> {t("intel.forecast.overdue")}</span>
              ) : r.predicted_next_date ? (
                <span className="badge-pill badge-approved">{t("intel.forecast.onTrack")}</span>
              ) : (
                <span className="badge-pill badge-neutral">{t("intel.forecast.noHistory")}</span>
              )}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
              <Metric label={t("intel.forecast.lastService")} value={r.last_service_date ? fmtDate(r.last_service_date) : "—"} />
              <Metric label={t("intel.forecast.daysSince")} value={r.days_since_last ?? "—"} />
              <Metric label={t("intel.forecast.avgInterval")} value={r.avg_interval_days ? `${r.avg_interval_days} d` : "—"} />
              <Metric label={t("intel.forecast.predictedNext")} value={r.predicted_next_date ? fmtDate(r.predicted_next_date) : "—"} highlight={overdue} />
              <Metric label={t("intel.forecast.tickets12mo")} value={r.services_last_12mo} />
              <Metric label={t("intel.forecast.cost12mo")} value={r.cost_last_12mo ? `€ ${r.cost_last_12mo.toFixed(2)}` : "—"} />
              <Metric label={t("intel.forecast.ticketsTotal")} value={r.ticket_count_total} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function UtilizationPanel({ t }) {
  const [year, setYear] = useState(new Date().getFullYear());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { (async () => {
    setLoading(true);
    try { const res = await api.get("/intelligence/utilization", { params: { year } }); setData(res.data); }
    finally { setLoading(false); }
  })(); }, [year]);

  if (loading) return <div className="ledger-card p-8 text-center text-[#94A3B8]">{t("common.loading")}</div>;
  if (!data) return null;

  const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

  return (
    <div className="space-y-4" data-testid="utilization-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <CalendarIcon className="w-4 h-4 text-[#4DA6FF]" />
          <div className="font-mono text-[10px] tracking-widest text-[#94A3B8]">{t("intel.utilization.title")}</div>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => setYear(y => y - 1)} data-testid="util-year-prev"
            className="p-1.5 rounded-md bg-[#050A14] border border-[#1E293B] hover:border-[#4DA6FF] text-[#94A3B8] hover:text-[#F8FAFC]"><ChevronLeft className="w-3 h-3" /></button>
          <div className="px-4 py-1 rounded-md bg-[#050A14] border border-[#1E293B] font-mono text-sm min-w-[70px] text-center" data-testid="util-year">{year}</div>
          <button onClick={() => setYear(y => y + 1)} data-testid="util-year-next"
            className="p-1.5 rounded-md bg-[#050A14] border border-[#1E293B] hover:border-[#4DA6FF] text-[#94A3B8] hover:text-[#F8FAFC]"><ChevronRight className="w-3 h-3" /></button>
        </div>
      </div>

      {/* Per-asset yearly summary + month bars */}
      {data.assets.map(a => (
        <div key={a.asset_id} className="ledger-card p-5" data-testid={`util-asset-${a.asset_id}`}>
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="font-display text-lg font-bold tracking-tight">{a.asset_name}</div>
              <div className="font-mono text-[10px] tracking-widest text-[#94A3B8]">
                {a.reserved_day_count} {t("intel.utilization.daysReserved")} · {a.utilization_pct}%
              </div>
            </div>
            <div className="w-28 h-2 bg-[#050A14] rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-[#10B981] to-[#4DA6FF]" style={{ width: `${Math.min(100, a.utilization_pct)}%` }} />
            </div>
          </div>
          <div className="grid grid-cols-12 gap-1">
            {a.by_month.map((m, i) => {
              const h = m.days_in_month ? Math.round(m.reserved_total * 100 / m.days_in_month) : 0;
              return (
                <div key={i} className="flex flex-col items-center gap-1" title={`${MONTHS[i]}: ${m.reserved_total}/${m.days_in_month} d (private ${m.private}, charter ${m.charter}, maint ${m.maintenance})`}>
                  <div className="w-full h-16 bg-[#050A14] border border-[#1E293B] rounded overflow-hidden flex flex-col justify-end relative">
                    <div className="bg-[#EF4444]" style={{ height: `${(m.maintenance * 100 / Math.max(1, m.days_in_month))}%` }} />
                    <div className="bg-[#10B981]" style={{ height: `${(m.charter * 100 / Math.max(1, m.days_in_month))}%` }} />
                    <div className="bg-[#4DA6FF]" style={{ height: `${(m.private * 100 / Math.max(1, m.days_in_month))}%` }} />
                  </div>
                  <div className="font-mono text-[9px] text-[#94A3B8]">{MONTHS[i]}</div>
                  <div className="font-mono text-[9px] text-[#F8FAFC]">{h}%</div>
                </div>
              );
            })}
          </div>
          <div className="flex items-center gap-3 text-[10px] font-mono text-[#94A3B8] mt-2">
            <span><span className="inline-block w-2 h-2 bg-[#4DA6FF] mr-1 rounded-sm" />{t("intel.utilization.private")}</span>
            <span><span className="inline-block w-2 h-2 bg-[#10B981] mr-1 rounded-sm" />{t("intel.utilization.charter")}</span>
            <span><span className="inline-block w-2 h-2 bg-[#EF4444] mr-1 rounded-sm" />{t("intel.utilization.maintenance")}</span>
          </div>
        </div>
      ))}

      {/* Prime-season idle opportunities */}
      <div className="ledger-card p-5" data-testid="util-idle">
        <div className="flex items-center gap-2 mb-3">
          <AlertCircle className="w-4 h-4 text-[#F59E0B]" />
          <div className="font-mono text-[10px] tracking-widest text-[#94A3B8]">{t("intel.utilization.idleTitle")}</div>
        </div>
        <p className="text-xs text-[#94A3B8] mb-3">{t("intel.utilization.idleIntro")}</p>
        {data.prime_season_idle_weekends.length === 0 ? (
          <div className="text-sm text-[#34D399] font-mono">{t("intel.utilization.noIdle")}</div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
            {data.prime_season_idle_weekends.slice(0, 24).map((w, i) => (
              <div key={i} className="bg-[#050A14] border border-[#F59E0B]/20 rounded-md p-2" data-testid={`util-idle-${i}`}>
                <div className="font-mono text-[10px] text-[#FBBF24]">{w.weekday.slice(0, 3)} · {fmtDate(w.date)}</div>
                <div className="text-xs">{w.asset_name}</div>
              </div>
            ))}
            {data.prime_season_idle_weekends.length > 24 && (
              <div className="col-span-full text-[10px] font-mono text-[#94A3B8] text-center mt-1">
                +{data.prime_season_idle_weekends.length - 24} {t("intel.utilization.moreIdle")}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function Metric({ label, value, highlight }) {
  return (
    <div className="bg-[#050A14] border border-[#1E293B] rounded-md p-2">
      <div className="font-mono text-[9px] tracking-widest text-[#94A3B8]">{label}</div>
      <div className={`text-sm font-medium ${highlight ? "text-[#F87171]" : "text-[#F8FAFC]"}`}>{value}</div>
    </div>
  );
}
